import React, { useEffect, useRef, useState, useCallback } from 'react';
import TextFiled from './TextFiled';
import { Autocomplete } from '@react-google-maps/api';
import ErrorBoundary from '../ErrorBoundary';
import { debounce } from 'lodash';
import { useGoogleMaps } from '../../../context/GoogleMapsProvider';
import { adminWizardInputClasses } from '../../../sections/AdminDashboard/shared/adminUx';

// Add styles to hide Google attribution
const styles = {
  container: {
    position: 'relative',
  },
  hideAttribution: {
    '& .pac-container:after': {
      display: 'none !important',
    },
  },
};

const LocationAutocomplete = ({ value, onChange, error, dashboard = false, className = '', ...props }) => {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const { isLoaded, loadError } = useGoogleMaps();
  const hasError = Boolean(error || apiError);
  const fieldClassName = dashboard
    ? `${adminWizardInputClasses(hasError)} ${className}`.trim()
    : undefined;
  const publicClassName = dashboard ? '' : className;
  const textFieldProps = {
    ...props,
    className: publicClassName,
    fieldClassName,
    fullWidth: dashboard ? false : props.fullWidth,
  };

  useEffect(() => {
    if (loadError) {
      setApiError('Failed to load Google Maps API');
    }
  }, [loadError]);

  const onLoad = (autocomplete) => {
    autocompleteRef.current = autocomplete;
  };

  const onPlaceChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      if (place.formatted_address) {
        onChange(place.formatted_address);
        setShowSuggestions(false);
      }
    }
  };

  const handleInputChange = (e) => {
    const input = e.target.value;
    setApiError(null);
    onChange(input);
    setShowSuggestions(true);
  };

  const handleBlur = () => {
    setTimeout(() => {
      setShowSuggestions(false);
    }, 200);
  };

  if (!isLoaded) {
    return <TextFiled {...textFieldProps} value={value} error={error || apiError} />;
  }

  return (
    <ErrorBoundary>
      <div className="relative w-full min-w-0" style={styles.container}>
        <style>
          {`
            .pac-container:after {
              display: none !important;
            }
            .pac-container {
              background-color: white;
              border-radius: 4px;
              box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
              margin-top: 4px;
              padding: 0;
              position: absolute;
              z-index: 1000;
            }
            .pac-item {
              cursor: pointer;
              padding: 8px 12px;
              border-top: 1px solid #e6e6e6;
            }
            .pac-item:hover {
              background-color: #f5f5f5;
            }
            .pac-item-query {
              font-size: 14px;
              color: #333;
            }
            .pac-matched {
              font-weight: bold;
            }
          `}
        </style>
        <Autocomplete
          onLoad={onLoad}
          onPlaceChanged={onPlaceChanged}
          options={{
            componentRestrictions: { country: 'au' },
            types: ['geocode', 'establishment'],
          }}
        >
          <TextFiled
            {...textFieldProps}
            value={value}
            onChange={handleInputChange}
            onBlur={handleBlur}
            inputRef={inputRef}
            error={error || apiError}
            aria-label="Location search"
            role="combobox"
            aria-expanded={showSuggestions}
            aria-autocomplete="list"
            // disabled={isLoading}
            autoComplete="off"
          />
        </Autocomplete>
      </div>
    </ErrorBoundary>
  );
};

export default LocationAutocomplete; 